<!-- eslint-disable no-undef -->
<script setup>
    // Imports
    import { ref, watch, onMounted, onUnmounted } from 'vue'; 
    import VueTailwindDatepicker from 'vue-tailwind-datepicker';

    // Props
    const props = defineProps({
        modelValue: {
            type: Array,
            default: () => []
        }
    });

    // Emits
    const emit = defineEmits(['update:modelValue']);

    // References
    const containerRef = ref(null);
    const inputValue = ref('');
    const dateValue = ref(props.modelValue);
    const validValue = ref(true);
    const openCalander = ref(false);
    const formatter = ref({
        date: "YYYY-MM-DD HH:mm:ss",
        month: "MMM"
    });

    // Globals
    const customShortcuts = () => [
        {
            label: "Today",
            atClick: () => {
                const startOfDay = new Date().setHours(0, 0, 0, 0);
                const now = new Date().setHours(23, 59, 59, 999);
                return [startOfDay, now];
            },
        },
        {
            label: "Yesterday",
            atClick: () => {
                const now = new Date();
                const yesterday = now.setDate(now.getDate() -1);

                const startOfDay = new Date(yesterday).setHours(0, 0, 0, 0);
                const endOfDay = new Date(yesterday).setHours(23, 59, 59, 999);

                return [startOfDay, endOfDay];
            },
        },
        {
            label: "Last 7 Days",
            atClick: () => {
                const now = new Date();
                const startDate = new Date(now.setDate(now.getDate() -7)).setHours(0, 0, 0, 0);
                const endDate = new Date().setHours(23, 59, 59, 999);
                return [startDate, endDate];
            },
        },
        {
            label: "Last 15 Days",
            atClick: () => {
                const now = new Date();
                const startDate = new Date(now.setDate(now.getDate() -15)).setHours(0, 0, 0, 0);
                const endDate = new Date().setHours(23, 59, 59, 999);
                return [startDate, endDate];
            },
        },
        {
            label: "This Month",
            atClick: () => {
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                return [startOfMonth, endOfMonth];
            },
        },
        {
            label: "Last Month",
            atClick: () => {
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
                return [startOfMonth, endOfMonth];
            },
        },
    ];
    const customTheme = {
        primary: '#4CAF50',    // Green for primary color
        secondary: '#FF5722',  // Orange for secondary color
        backgroundColor: '#FFFFFF', // White background
        textColor: '#000000',  // Black text
        borderColor: '#E0E0E0' // Light gray borders
    };
    const dateFormat = /^(20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01]) (0[0-9]|1[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]) ~ (20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01]) (0[0-9]|1[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;

    // Lifecycle Hooks
    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside)
    });

    // Format a date value (string, timestamp, or Date) to "YYYY-MM-DD HH:mm:ss"
    const formatToDateString = (val) => {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    // Watchers
    watch(dateValue, (newValue) => {
        // When dates are selected using the calendar or a shortcut, format each value
        // as "YYYY-MM-DD HH:mm:ss" so the inputValue passes the regex and emits correctly.
        if (!newValue || newValue.length === 0) {
            inputValue.value = '';
            return;
        }
        const parts = newValue.map(formatToDateString);
        inputValue.value = parts.join(' ~ ');
    });

    watch(inputValue, (newValue) => {
        // When changes are made to the text field, emit the newValue if
        // it's in the correct format.

        // Confirm the textbox value follows the proper format.
        if (dateFormat.test(newValue)) {

            // Check to see if the first date is less than the second date.
            const [dateTime1, dateTime2] = newValue.split(' ~ ');
            const date1 = new Date(dateTime1);
            const date2 = new Date(dateTime2);
            if (date1 < date2) {
                emit('update:modelValue', newValue.split(' ~ '));
                validValue.value = true;
            }
            else {
                validValue.value = false;
            }
            
        }
        else if (newValue === '') {
            validValue.value = true;
        }
        else {
            validValue.value = false;
        }
    });

    // Methods
    const handleClickOutside = (event) => {
        if (containerRef.value && !containerRef.value.contains(event.target)) {
            openCalander.value = false;
        }
    };

    const close = () => {
        openCalander.value = false;
    };

    defineExpose({ close });

</script>

<template>
    <div ref="containerRef" class="relative">
        <div class="relative">
            <input
                type="text"
                placeholder="YYYY-MM-DD HH:mm:ss ~ YYYY-MM-DD HH:mm:ss"
                v-model="inputValue"
                @click.stop="openCalander = true"
                class="border border-black p-2 pr-9 rounded w-full"
                :class="[validValue ? 'bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400' : 'bg-red-300 dark:bg-red-700']"
            />
            <button
                type="button"
                @click.stop="openCalander = !openCalander"
                class="absolute inset-y-0 right-0 flex items-center px-2.5 border border-t-black border-r-black border-b-black rounded-r-sm bg-gray-100 dark:bg-gray-800 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                tabindex="-1"
                title="Toggle calendar"
            >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            </button>
        </div>
        <VueTailwindDatepicker
            v-if="openCalander"
            :formatter="formatter" 
            :shortcuts="customShortcuts"
            :theme="customTheme"
            v-model="dateValue"
            no-input
            class="absolute mt-1 z-50"
        ></VueTailwindDatepicker>
    </div>
</template>